import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./stage5.css";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";

const STAGE_NUM = 5;

const STAGE4_BG = `${process.env.PUBLIC_URL}/assets/game/stage4-bg.jpg`;
const STAGE4_BG_MOBILE = `${process.env.PUBLIC_URL}/assets/game/stage4_mob.png`;
const CURSOR_URL = `${process.env.PUBLIC_URL}/assets/game/cursor.png`;
const BIG_PICTURE_URL = `${process.env.PUBLIC_URL}/assets/game/big-picture.jpg`;

const MOBILE_PORTRAIT_MQ = "(max-width: 768px) and (orientation: portrait)";

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

export default function Stage5() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);

  const [dbReady, setDbReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const otherStagesTotalRef = useRef(0);
  const submittedRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
      cursor: body.style.cursor,
    };
    // old/puzzle-img.php reuses stage4's exact body styling (bg + custom cursor).
    const applyBackground = () => {
      const isMobilePortrait = window.matchMedia(MOBILE_PORTRAIT_MQ).matches;
      body.style.backgroundImage = `linear-gradient(rgba(0, 0, 0, .4), rgba(0, 0, 0, .4)), url("${
        isMobilePortrait ? STAGE4_BG_MOBILE : STAGE4_BG
      }")`;
      body.style.backgroundSize = isMobilePortrait ? "cover" : "100% 100%";
      body.style.backgroundRepeat = "no-repeat";
      body.style.backgroundPosition = "center center";
      body.style.backgroundAttachment = "fixed";
    };

    html.classList.add("stage5-lock");
    body.classList.remove("common-bg");
    body.classList.add("stage5-reveal-page");
    applyBackground();
    body.style.cursor = `url("${CURSOR_URL}"), auto`;
    window.addEventListener("resize", applyBackground);
    window.addEventListener("orientationchange", applyBackground);
    return () => {
      html.classList.remove("stage5-lock");
      body.classList.remove("stage5-reveal-page");
      window.removeEventListener("resize", applyBackground);
      window.removeEventListener("orientationchange", applyBackground);
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
      body.style.cursor = prev.cursor;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }
      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        const rowId = sessionStorage.getItem(STAGE_ROW_KEY);
        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));
        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage5 = Number(st.stage5) || 0;
          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage5);
        }

        setStageActive(STAGE_NUM, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("Stage5 loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, isDemoBypass, storedUser, navigate]);

  const finishStage = useCallback(async () => {
    if (submitting || submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);

    const uid = storedUser.userId || storedUser.userid || storedUser.id;
    const sessionId = storedUser.sessionId;
    const organizationId = storedUser.organizationId;
    const timeStr = formatSecondsToClock(elapsedRef.current);
    // old/puzzle-img.php awards no points — it's a pure reveal.
    const finalTotal = otherStagesTotalRef.current;
    const stageId = sessionStorage.getItem(STAGE_ROW_KEY);

    if (!isDemoBypass && backendBase && adminToken && stageId) {
      try {
        await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: STAGE_NUM,
          score: 0,
          time: timeStr,
          total_score: finalTotal,
        });
        // The real "gameover" moment — old/puzzle-img.php is the last screen
        // before thankyou.php, so this is where the game is actually finalized.
        await fetch(`${backendBase}/welcomeStageComplete`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId,
            time: timeStr,
            total_score: finalTotal,
            points: finalTotal,
          }),
        });
      } catch (err) {
        console.error("Stage5 finish:", err);
      }
    }

    clearStageActive(STAGE_NUM);
    markStageCompleted(STAGE_NUM, storedUser);

    if (isDemoBypass) {
      window.location.href =
        String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
      return;
    }
    navigate("/thankyou");
  }, [submitting, storedUser, isDemoBypass, backendBase, adminToken, navigate]);

  if (!dbReady) {
    return <div className="stage5-loading">Loading game…</div>;
  }

  return (
    <div className="stage5-page">
      <img src={BIG_PICTURE_URL} alt="Your completed contribution" className="stage5-big-picture" />
      <button
        type="button"
        className="stage5-next-btn"
        disabled={submitting}
        onClick={finishStage}
      >
        {submitting ? "Loading…" : "Next"}
      </button>
    </div>
  );
}
